import type {CrmOverviewTimeSeriesPointV4} from "../../models/crm-v4";

/**
 * The overview metrics that are plotted over time. One id names the bucket field on
 * `CrmOverviewTimeSeriesPointV4`, the stat tile it shares its headline with, and the target definition its
 * budget is resolved from — so a card cannot read one metric and be judged against another.
 */
export type CrmTrendMetricKey = Extract<
    keyof CrmOverviewTimeSeriesPointV4,
    "adr" | "revpar" | "occupancy" | "totalRevenue"
>;

export interface CrmTrendFormatContext {
    locale: string;
    currency: string;
}

export interface CrmTrendFormats {
    /** Full precision: the hover, the table and the legend. */
    value: (value: number) => string;
    /** Compact, for the axis ticks, where a full currency figure per tick would not fit. */
    tick: (value: number) => string;
}

export interface CrmTrendMetricDefinition {
    key: CrmTrendMetricKey;
    /** App-owned copy, untranslated as in `overviewStatMetrics.ts`; the card runs it through `t`. */
    title: string;
    hint: string;
    formats: (context: CrmTrendFormatContext) => CrmTrendFormats;
}

/** Money reads in full on the hover and compact on the axis; how many decimals is the metric's own call. */
const currencyFormats = (fractionDigits: number) => ({locale, currency}: CrmTrendFormatContext): CrmTrendFormats => {
    const value = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        maximumFractionDigits: fractionDigits,
    });
    const tick = new Intl.NumberFormat(locale, {
        style: "currency",
        currency,
        notation: "compact",
        maximumFractionDigits: 1,
    });

    return {value: amount => value.format(amount), tick: amount => tick.format(amount)};
};

/** A share is short enough to read the same way on the axis and on the hover. */
const percentFormats = ({locale}: CrmTrendFormatContext): CrmTrendFormats => {
    const number = new Intl.NumberFormat(locale, {maximumFractionDigits: 1});
    const format = (value: number) => `${number.format(value)}%`;

    return {value: format, tick: format};
};

export const CRM_TREND_METRICS: Record<CrmTrendMetricKey, CrmTrendMetricDefinition> = {
    adr: {
        key: "adr",
        title: "ADR over time",
        hint: "Average room revenue per occupied room night in each period",
        formats: currencyFormats(2),
    },
    revpar: {
        key: "revpar",
        title: "RevPAR over time",
        hint: "Room revenue per available room night in each period",
        formats: currencyFormats(2),
    },
    occupancy: {
        key: "occupancy",
        title: "Occupancy over time",
        hint: "Share of available room nights occupied in each period",
        formats: percentFormats,
    },
    totalRevenue: {
        key: "totalRevenue",
        // A running total, so the whole series is the period's progress rather than one bucket's takings.
        title: "Total revenue over time",
        hint: "Cumulative revenue and budget progress in the selected period",
        formats: currencyFormats(0),
    },
};
